"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.httpDownload = void 0;
var index_1 = require("../index");
var helpers_1 = require("../helpers/helpers");
var messages_1 = require("../constants/messages");
var core_1 = require("./core");
var http_gateway_sdk_js_1 = require("@ibm-aspera/http-gateway-sdk-js");
/**
 * HTTP Gateway Download Logic for presigned flow
 * Presigned flow is when used files are too large or unknown file size
 *
 * @param transferSpec - TransferSpec for the download
 * @param asperaSdkSpec IBM Aspera settings when starting a transfer.
 *
 * @returns Promise that resolves on success invoke or rejects if unable to start
 *
 * @remarks
 * This function is used internally and not exported.
 */
var httpDownloadPresigned = function (transferSpec, asperaSdkSpec) {
    // create a transfer sdk object
    var transferObject = (0, core_1.getSdkTransfer)(transferSpec);
    transferObject.httpDownloadExternalHandle = true;
    (0, core_1.sendTransferUpdate)(transferObject);
    var triggerFailed = function (error) {
        var errorData = (0, core_1.getMessageFromError)(error.response || error);
        transferObject.status = 'failed';
        transferObject.error_code = errorData.code;
        transferObject.error_desc = errorData.message;
        (0, core_1.sendTransferUpdate)(transferObject);
    };
    var url = new URL((asperaSdkSpec === null || asperaSdkSpec === void 0 ? void 0 : asperaSdkSpec.http_gateway_override_server_url) || index_1.asperaSdk.globals.httpGatewayUrl);
    var headers = {
        'Content-Type': 'application/json',
        accept: 'application/json',
    };
    if (asperaSdkSpec === null || asperaSdkSpec === void 0 ? void 0 : asperaSdkSpec.http_gateway_authentication) {
        headers['Authorization'] = "Bearer ".concat(asperaSdkSpec.http_gateway_authentication.token);
        headers['X-Aspera-AccessKey'] = asperaSdkSpec.http_gateway_authentication.access_key;
    }
    var protocol = url.protocol === 'https:' ? 'https' : 'http';
    return fetch("".concat(url.toString(), "/presign"), {
        method: 'POST',
        headers: headers,
        body: (0, helpers_1.safeJsonString)({
            transfer_spec: transferSpec,
            method: 'GET',
            protocol: protocol,
            headers: {
                host: url.host,
            },
        }),
    }).then(function (response) {
        return response.json().then(function (data) {
            return {
                headers: response.headers,
                body: data,
                status: response.status,
            };
        });
    }).then(function (response) {
        if (response.status >= 400) {
            triggerFailed(response.body);
            return transferObject;
        }
        transferObject.httpRequestId = response.headers.get('X-Request-Id');
        transferObject.status = 'running';
        (0, core_1.sendTransferUpdate)(transferObject);
        var iframe = document.createElement('iframe');
        iframe.src = response.body.signed_url;
        iframe.width = '1px';
        iframe.height = '1px';
        index_1.asperaSdk.globals.httpGatewayIframeContainer.appendChild(iframe);
        return transferObject;
    }).catch(function (error) {
        triggerFailed(error);
        return transferObject;
    });
};
/**
 * HTTP Gateway Download Logic for in browser download with progress
 * This is used when the transfer size is known and under the threshold
 *
 * @param transferSpec - TransferSpec for the download
 * @param asperaSdkSpec IBM Aspera settings when starting a transfer.
 *
 * @returns Promise that resolves on success invoke or rejects if unable to start
 *
 * @remarks
 * This function is used internally and not exported.
 */
var httpDownloadInBrowser = function (transferSpec, asperaSdkSpec) {
    // create a transfer sdk object
    var transferObject = (0, core_1.getSdkTransfer)(transferSpec);
    var triggerFailed = function (error) {
        var errorData = (0, core_1.getMessageFromError)(error.response || error);
        transferObject.status = 'failed';
        transferObject.error_code = errorData.code;
        transferObject.error_desc = errorData.message;
        (0, core_1.sendTransferUpdate)(transferObject);
    };
    (0, core_1.sendTransferUpdate)(transferObject);
    var headers = {
        'X-Aspera-Spec': (0, core_1.base64Encoding)((0, helpers_1.safeJsonString)(transferSpec)),
    };
    if (asperaSdkSpec === null || asperaSdkSpec === void 0 ? void 0 : asperaSdkSpec.http_gateway_authentication) {
        headers['Authorization'] = "Bearer ".concat(asperaSdkSpec.http_gateway_authentication.token);
        headers['X-Aspera-AccessKey'] = asperaSdkSpec.http_gateway_authentication.access_key;
    }
    fetch("".concat((asperaSdkSpec === null || asperaSdkSpec === void 0 ? void 0 : asperaSdkSpec.http_gateway_override_server_url) || index_1.asperaSdk.globals.httpGatewayUrl, "/download"), { method: 'GET', headers: headers }).then(function (data) {
        var responseHeaders = data.headers;
        transferObject.httpRequestId = responseHeaders.get('X-Request-Id');
        var chunks = [];
        var reader = data.body.getReader();
        transferObject.status = 'running';
        (0, core_1.sendTransferUpdate)(transferObject);
        transferObject.bytes_expected = Number(responseHeaders.get('Content-Length') || 0);
        var readBytes = function () {
            reader.read().then(function (content) {
                var _a;
                if (content.done) {
                    if (data.status >= 400 || !transferObject.bytes_written) {
                        triggerFailed(data.body);
                        return;
                    }
                    transferObject.status = 'completed';
                    (0, core_1.sendTransferUpdate)(transferObject);
                    var blobData = new Blob(chunks, { type: responseHeaders.get('Content-Type') });
                    var objectURL = URL.createObjectURL(blobData);
                    var a = document.createElement('a');
                    a.href = objectURL;
                    a.setAttribute('style', 'display: none;');
                    a.download = (responseHeaders.get('Content-Disposition') || 'download').replace('attachment; filename="', '').replace('"', '');
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(objectURL);
                }
                else {
                    chunks.push(content.value);
                    transferObject.status = 'running';
                    transferObject.bytes_written += ((_a = content.value) === null || _a === void 0 ? void 0 : _a.length) || 0;
                    transferObject.percentage = (transferObject.bytes_written / transferObject.bytes_expected);
                    (0, core_1.sendTransferUpdate)(transferObject);
                    readBytes();
                }
            }).catch(function (error) {
                triggerFailed(error);
            });
        };
        readBytes();
    }).catch(function (error) {
        triggerFailed(error);
    });
    return Promise.resolve(transferObject);
};
/**
 * HTTP Gateway Download Logic
 *
 * @param transferSpec - TransferSpec for the download
 * @param asperaSdkSpec IBM Aspera settings when starting a transfer.
 *
 * @returns Promise that resolves on success invoke or rejects if unable to start
 *
 * @remarks
 * Most logic is called directly by Desktop SDK functions
 * You may not need to import anything from this file.
 */
var httpDownload = function (transferSpec, asperaSdkSpec) {
    if (!index_1.asperaSdk.httpGatewayIsReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified, { type: 'download' });
    }
    if (index_1.asperaSdk.useOldHttpGateway) {
        return (0, http_gateway_sdk_js_1.download)(transferSpec);
    }
    if (transferSpec.tags &&
        transferSpec.tags.aspera &&
        transferSpec.tags.aspera['http-gateway'] &&
        transferSpec.tags.aspera['http-gateway'].expected_size <= index_1.asperaSdk.httpGatewayInBrowserDownloadThreshold) {
        return httpDownloadInBrowser(transferSpec, asperaSdkSpec);
    }
    else {
        return httpDownloadPresigned(transferSpec, asperaSdkSpec);
    }
};
exports.httpDownload = httpDownload;
